/**
 * DERIVED CSS colour variables
 * ============================================================================
 * Source of truth: ./colors.js (lightColors / darkColors)
 *
 * Do NOT hardcode colour values here. Map token paths → CSS custom properties only.
 * Applied at runtime by ThemeProvider via applyCssColorVariables(mode).
 */

import { getColorPalette } from './colors';

/**
 * Build the CSS custom-property map for a theme mode.
 * @param {'light'|'dark'} [mode='light']
 * @returns {Record<string, string>}
 */
export const getCssColorVariables = (mode = 'light') => {
  const p = getColorPalette(mode);

  return {
    '--color-primary-main': p.primary.main,
    '--color-primary-dark': p.primary.dark,
    '--color-primary-light': p.primary.light,
    '--color-primary-contrast': p.primary.contrastText,

    '--color-secondary-main': p.secondary.main,
    '--color-secondary-dark': p.secondary.dark,
    '--color-secondary-light': p.secondary.light,

    '--color-success-main': p.status.success,
    '--color-success-dark': p.status.successDark,
    '--color-warning': p.status.warning,
    '--color-warning-main': p.status.warning,
    '--color-warning-dark': p.status.warningDark,
    '--color-error-main': p.status.error,
    '--color-error-dark': p.status.errorDark,
    '--color-info-main': p.status.info,
    '--color-info-dark': p.status.infoDark,

    '--color-background-default': p.background.default,
    '--color-background-paper': p.background.paper,

    '--color-text-primary': p.text.primary,
    '--color-text-secondary': p.text.secondary,
    '--color-text-disabled': p.text.disabled,

    '--color-grey-background': p.grey.background,
    '--color-grey-border': p.grey.border,
    '--color-grey-border-dark': p.grey.borderDark,
    '--color-grey-dark': p.grey.dark,

    '--color-common-black': p.common.black,
    '--color-common-white': p.common.white,

    '--color-action-hover': p.action.hover,
    '--color-action-selected': p.action.selected,

    '--color-glass-bg': p.effects.glassBg,
    '--color-glass-hover': p.effects.glassHover,
    '--color-chip-bg': p.effects.chipBg,
    '--color-chip-text': p.effects.chipText,
    '--color-chip-border': p.effects.chipBorder,
    '--secondary-glow': p.effects.secondaryGlow,
    '--color-shadow': p.effects.shadow,
  };
};

/**
 * Apply derived CSS colour variables to document.documentElement.
 * Safe to call on every mode change; replaces previous colour vars.
 * @param {'light'|'dark'} [mode='light']
 */
export const applyCssColorVariables = (mode = 'light') => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const vars = getCssColorVariables(mode);

  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
};

export default getCssColorVariables;
